import { Node, Edge } from "@xyflow/react";
import { atom } from "jotai";
import { NodeType, DefaultNodeData, PathNodeData } from "../types";
import { takeSnapshotSetter } from "../diagramming";
import { nodesAtom } from "../nodes";
import { edgesAtom } from "../edges";
import {
  calculatePathChildrenYPositions,
  getAbsoluteNodePosition,
  getCenteredYPosition,
  uuid,
} from "../utils";
import { GAP_BETWEEN_NODES } from "@zapier/canvas-constants";
import { MARKER } from "../constants";
import { saveCanvasSetter } from "../persistence";

type Params = {
  sourceNodeId: string | null;
  nodePartial: Partial<Node<PathNodeData>>;
  numOfPaths: number;
  shouldAddIncomerToPathNodeEdge?: boolean;
};

export const addAndSavePathNodeSetter = atom(
  null,
  (
    get,
    set,
    {
      sourceNodeId,
      nodePartial,
      numOfPaths,
      shouldAddIncomerToPathNodeEdge = true,
    }: Params,
  ) => {
    set(takeSnapshotSetter);

    const nodes = get(nodesAtom);
    const edges = get(edgesAtom);

    /** If sourceNodeId = null, sourceNode is the path node itself when you drop a path node without connecting it to another node */
    const sourceNode = sourceNodeId
      ? nodes.find((node) => node.id === sourceNodeId)!
      : nodePartial;
    const newNodes: Node<DefaultNodeData | PathNodeData>[] = [];
    const newEdges: Edge[] = [];
    const pathNodeId = nodePartial.id ?? uuid();

    const currentMousePosition = {
      x: sourceNode.position!.x,
      y: sourceNode.position!.y,
    };

    const sourceNodePosition = sourceNodeId
      ? getAbsoluteNodePosition(sourceNode as Node, nodes)
      : currentMousePosition;

    const position = sourceNodeId
      ? {
          x: sourceNodePosition.x + GAP_BETWEEN_NODES,
          y: getCenteredYPosition({
            nodePosition: sourceNodePosition,
            nodeType: sourceNode.type as NodeType,
            isPathNode: true,
          }),
        }
      : currentMousePosition;

    newNodes.push({
      ...nodePartial,
      id: pathNodeId,
      position,
      data: { label: "" },
      selected: false,
    });

    if (sourceNodeId && shouldAddIncomerToPathNodeEdge) {
      const incomerToPathNodeEdge: Edge = {
        id: uuid(),
        source: sourceNodeId,
        target: pathNodeId,
        sourceHandle: "right",
        targetHandle: "left",
        markerEnd: MARKER,
      };

      newEdges.push(incomerToPathNodeEdge);
    }

    /* When you add a path node from diagramming toolbar without a connecting node,
       we add offset so the children steps look even
     */
    const yOffset = sourceNodeId ? 0 : 34;

    const childYPositions = calculatePathChildrenYPositions(
      sourceNodePosition.y + yOffset,
      numOfPaths,
    );

    for (let i = 0; i < numOfPaths; i++) {
      const pathNodeChildId = uuid();
      const childPathNode: Node<DefaultNodeData> = {
        id: pathNodeChildId,
        position: {
          x:
            sourceNodePosition.x +
            (sourceNodeId ? GAP_BETWEEN_NODES * 2 : GAP_BETWEEN_NODES),
          y: childYPositions[i],
        },
        type: "default",
        data: {
          label: "",
          ...nodePartial.data,
          attributionSourceType: "MANUAL",
        },
        // Inherit parentId from the path node so child nodes are positioned correctly
        // relative to the group when the path node is inside a group. Without this,
        // child positions would be treated as absolute coordinates, causing them to
        // appear far away from the group.
        ...(nodePartial.parentId && { parentId: nodePartial.parentId }),
      };

      newNodes.push(childPathNode);

      const pathNodeToOutgoerEdge: Edge = {
        id: uuid(),
        source: pathNodeId,
        target: pathNodeChildId,
        sourceHandle: "right",
        targetHandle: "left",
        markerEnd: MARKER,
        selected: false,
      };

      newEdges.push(pathNodeToOutgoerEdge);
    }

    let updatedNewNodes = nodes;
    let updatedNewEdges = edges;

    set(edgesAtom, (edges) => {
      updatedNewEdges = edges.concat(newEdges);
      return updatedNewEdges;
    });

    set(nodesAtom, (nodes) => {
      const deselectedNodes: Node[] = nodes.map((node) => ({
        ...node,
        selected: false,
      }));
      updatedNewNodes = deselectedNodes.concat(newNodes);
      return updatedNewNodes;
    });

    set(saveCanvasSetter, {
      nodes: updatedNewNodes,
      edges: updatedNewEdges,
    });
  },
);

addAndSavePathNodeSetter.debugLabel = "addAndSavePathNodeSetter";
